import React, { Suspense, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, useAuthStatus } from "../context/UserContext";
import { PublicRoutes } from "./PublicRoutes";
import { ScrollToTopOnNavigate } from "./ScrollToTopOnNavigate";
import { POST_AUTH_REDIRECT_KEY } from "../authentication/postAuthDestination";
import { usePageTitle } from "./usePageTitle";
import { Header } from "./Header";
import { useIsPrivatePath } from "./useIsPrivatePath";

/**
 * The signed-in application: Cloudscape AppLayout, the side navigation and every
 * dashboard route. Split out because it is the single largest thing in the bundle
 * and no anonymous visitor to a marketing page ever renders it.
 */
const AppShell = React.lazy(() => import("./AppShell"));

/**
 * The sign-in modal: Cloudscape form controls plus the Amplify auth flows. Only
 * loaded once the visitor actually asks to sign in.
 */
const SignInModal = React.lazy(() => import("../authentication/SignInModal"));

interface NavigationComponentProps {
    showSignIn: boolean;
    setShowSignIn(value: boolean): void;
}

const NavigationComponent: React.FC<NavigationComponentProps> = ({ showSignIn, setShowSignIn }) => {
    const user = useUser();
    const authStatus = useAuthStatus();
    const navigate = useNavigate();
    usePageTitle();
    const onPrivatePath = useIsPrivatePath();

    useEffect(() => {
        if (!!user.email) {
            setShowSignIn(false);
            const redirect = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
            if (redirect) {
                sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
                navigate(redirect);
            }
        }
    }, [user, setShowSignIn, navigate]);

    /*
     * Which shell to render.
     *
     * Marketing pages are prerendered anonymously, so rendering the public tree
     * whenever we are not certain the visitor is signed in keeps the first client
     * render identical to the HTML that was served — which is what lets index.tsx
     * hydrate instead of rebuilding the DOM.
     *
     * The one case that needs care is a signed-in visitor loading a dashboard URL
     * directly. Those paths have no prerendered HTML and no public route, so
     * rendering the public tree there would show an empty page for as long as the
     * session lookup takes. `PRIVATE_PATHS` is checked to show a real loading
     * state for exactly that case instead.
     */
    const isAuthenticated = authStatus === 'authenticated';
    const awaitingSessionOnPrivatePath = authStatus === 'checking' && onPrivatePath;

    return (
        <>
            {/* Renders nothing; resets scroll when the route changes. */}
            <ScrollToTopOnNavigate />
            <Header />
            {showSignIn && (
                <Suspense fallback={null}>
                    <SignInModal onDismiss={() => setShowSignIn(false)} />
                </Suspense>
            )}
            {isAuthenticated ? (
                <Suspense fallback={<PrivateRouteFallback />}>
                    <AppShell user={user} />
                </Suspense>
            ) : awaitingSessionOnPrivatePath ? (
                <PrivateRouteFallback />
            ) : (
                <main style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 56px)' }}>
                    {/*
                      Deliberately NOT wrapped in <Suspense>.

                      Our prerendered HTML is produced by client-side rendering inside
                      puppeteer, not by React's server renderer, so it carries none of
                      the <!--$--> boundary comments that hydration uses to locate a
                      Suspense boundary in the DOM. A <Suspense> here therefore fails
                      to hydrate even when nothing suspends, and React falls back to
                      rebuilding the whole tree — measurably, in
                      scripts/verify-hydration.js.

                      Nothing in this subtree needs a boundary anyway: index.tsx
                      preloads the current route's chunk before mounting, so
                      lazyWithPreload resolves synchronously. The boundaries below for
                      AppShell and SignInModal are safe because neither is part of the
                      first render — one waits on an authenticated session, the other
                      on a click.
                    */}
                    <PublicRoutes />
                </main>
            )}
        </>
    );
}

/**
 * Placeholder shown under the real header while a signed-in-only route resolves
 * its session or loads its chunk. Sized to the viewport minus the 56px header so
 * it does not collapse the page and shift the footer.
 */
const PrivateRouteFallback: React.FC = () => (
    <main
        style={{ minHeight: 'calc(100vh - 56px)' }}
        aria-busy="true"
        data-testid="private-route-fallback"
    />
);

export default NavigationComponent;
